import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import ArticleCard from '../components/ArticleCard';
import CategoryBadge from '../components/CategoryBadge';

export default function BlogCategory() {
  const { slug } = useParams();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ITEMS_PER_PAGE = 12;

  // Fetch articles by category
  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.get('/api/blog/artigos', {
          params: {
            categoria: slug,
            page: currentPage,
            limit: ITEMS_PER_PAGE,
          },
        });
        setArticles(data.artigos || []);
        setTotalPages(data.totalPages || 1);
      } catch (err) {
        setError('Erro ao carregar artigos da categoria. Tente novamente.');
        console.error('Error fetching articles:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [slug, currentPage]);

  // Fetch all categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await axios.get('/api/blog/categorias');
        setCategories(data.categorias || []);

        // Find current category info
        const current = (data.categorias || []).find(
          (cat) => cat.slug === slug
        );
        setCurrentCategory(current || null);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();
  }, [slug]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Breadcrumb Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-blue-600 transition">
              Home
            </Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-blue-600 transition">
              Blog
            </Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">
              {currentCategory?.nome || 'Categoria'}
            </span>
          </nav>
        </div>
      </div>

      {/* Category Header */}
      {currentCategory && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex items-center gap-4 mb-4">
              <CategoryBadge categoria={currentCategory} />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {currentCategory.nome}
            </h1>
            {currentCategory.descricao && (
              <p className="text-lg text-gray-600 max-w-2xl">
                {currentCategory.descricao}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Articles Grid */}
          <div className="lg:col-span-3">
            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-600">Carregando artigos...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && articles.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-600 text-lg mb-4">
                  Nenhum artigo encontrado nesta categoria.
                </p>
                <Link
                  to="/blog"
                  className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Voltar ao Blog
                </Link>
              </div>
            )}

            {/* Articles Grid */}
            {!loading && articles.length > 0 && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-12">
                  {articles.map((article) => (
                    <ArticleCard key={article.id} artigo={article} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-12">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 rounded-lg font-medium transition ${
                        currentPage === 1
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Anterior
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-4 py-2 rounded-lg font-medium transition ${
                            page === currentPage
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 rounded-lg font-medium transition ${
                        currentPage === totalPages
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Próximo
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Categories Widget */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Categorias
              </h3>
              <div className="space-y-3">
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/blog/categoria/${category.slug}`}
                    className={`block px-4 py-3 rounded-lg font-medium transition ${
                      category.slug === slug
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {category.nome}
                    {category.count && (
                      <span className="ml-2 text-sm opacity-75">
                        ({category.count})
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>

            {/* CTA Widget */}
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg shadow-sm p-6 text-white">
              <h3 className="text-lg font-bold mb-3">Receba Novidades</h3>
              <p className="text-blue-100 text-sm mb-4">
                Inscreva-se para receber os últimos artigos desta categoria
              </p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  // Handle subscription
                }}
                className="space-y-3"
              >
                <input
                  type="email"
                  placeholder="Seu email"
                  className="w-full px-4 py-2 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <button
                  type="submit"
                  className="w-full px-4 py-2 bg-white text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition"
                >
                  Inscrever
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
